import * as React from 'react';
import { Rnd, RndDragCallback, RndResizeCallback } from 'react-rnd';
import { Dispatch } from 'react-redux';

import { StyledMediaRnd } from './styles';
import { StoreState } from '../../../../reducers';
import { getBorderWidth, getRoundedValue, PositionAndSize } from '../../../../containers/Editor/utils/helpers';
import { updateEditableMedia } from '../../../../containers/Editor/actions';
import keys from '../../../../utils/keys';
import Utils from '../../../../utils';

interface MediaRndProps {
	page: number;
	id: string;
	frameWidth: number;
	frameHeight: number;
	width: number;
	height: number;
	x: number;
	y: number;
	scale: number;
	isActive: boolean;
	isLoaded: boolean;
	isKeyActionsDisabled: boolean;
	children: React.ReactNode;
	dispatch: Dispatch<StoreState>;
	isCustom?: boolean;
	onClickOutside?: (positionAndSize: PositionAndSize) => void;
	iframeContainer?: HTMLIFrameElement | null;
	isPercentageValues?: boolean;
}

interface MediaRndState extends PositionAndSize {
	isLoaded?: boolean;
}

interface ControlStylesData {
	bottom: React.CSSProperties;
	bottomLeft: React.CSSProperties;
	bottomRight: React.CSSProperties;
	left: React.CSSProperties;
	right: React.CSSProperties;
	top: React.CSSProperties;
	topLeft: React.CSSProperties;
	topRight: React.CSSProperties;
}

class MediaRnd extends React.Component<MediaRndProps, MediaRndState> {
	static getResizeControlStyles(scale: number): ControlStylesData {
		const DEFAULT_SIZE = 5;
		const DEFAULT_POSITION = 3;
		const DEFAULT_BORDER_WIDTH = 1.5;
		const MIN_SCALE = 1;
		const correctedScale = Math.max(MIN_SCALE, getBorderWidth(scale, DEFAULT_BORDER_WIDTH));

		const size = {
			width: `${DEFAULT_SIZE * correctedScale}px`,
			height: `${DEFAULT_SIZE * correctedScale}px`,
		};

		return {
			bottom: {
				...size,
				bottom: `-${DEFAULT_POSITION * correctedScale}px`,
				left: '50%',
				transform: 'translateX(-50%)',
			},
			bottomLeft: {
				...size,
				bottom: `-${DEFAULT_POSITION * correctedScale}px`,
				left: `-${DEFAULT_POSITION * correctedScale}px`,
			},
			bottomRight: {
				...size,
				bottom: `-${DEFAULT_POSITION * correctedScale}px`,
				right: `-${DEFAULT_POSITION * correctedScale}px`,
			},
			left: {
				...size,
				top: '50%',
				left: `-${DEFAULT_POSITION * correctedScale}px`,
				transform: 'translateY(-50%)',
			},
			right: {
				...size,
				top: '50%',
				right: `-${DEFAULT_POSITION * correctedScale}px`,
				transform: 'translateY(-50%)',
			},
			top: {
				...size,
				top: `-${DEFAULT_POSITION * correctedScale}px`,
				left: '50%',
				transform: 'translateX(-50%)',
			},
			topLeft: {
				...size,
				top: `-${DEFAULT_POSITION * correctedScale}px`,
				left: `-${DEFAULT_POSITION * correctedScale}px`,
			},
			topRight: {
				...size,
				top: `-${DEFAULT_POSITION * correctedScale}px`,
				right: `-${DEFAULT_POSITION * correctedScale}px`,
			},
		};
	}

	static getSizeInPx(sizeInPercent: number, frameSizeInPx: number): number {
		return (sizeInPercent / 100) * frameSizeInPx;
	}

	static getSizeInPercent(sizeInPx: number, frameSizeInPx: number): number {
		return (sizeInPx / frameSizeInPx) * 100;
	}

	constructor(props: MediaRndProps) {
		super(props);

		const { frameWidth, frameHeight, width, height, x, y, isLoaded, isPercentageValues } = props;

		this.state = {
			width: isPercentageValues ? MediaRnd.getSizeInPx(width, frameWidth) : width,
			height: isPercentageValues ? MediaRnd.getSizeInPx(height, frameHeight) : height,
			x: isPercentageValues ? MediaRnd.getSizeInPx(x, frameWidth) : x,
			y: isPercentageValues ? MediaRnd.getSizeInPx(y, frameHeight) : y,
			isLoaded,
		};
	}

	componentDidMount() {
		window.addEventListener('keydown', this.handleKeyDown);

		const { iframeContainer } = this.props;

		if (iframeContainer) {
			iframeContainer.contentDocument!.addEventListener('keydown', this.handleKeyDown);
		}
	}

	componentDidUpdate(prevProps: MediaRndProps) {
		const { isLoaded, isActive, isPercentageValues, frameWidth, frameHeight, width, height, x, y } = this.props;
		// as a result of undo/redo actions
		const isSizeOrPositionChanged = width !== prevProps.width ||
			height !== prevProps.height ||
			x !== prevProps.x ||
			y !== prevProps.y;
		const isNewMediaLoaded = isLoaded !== prevProps.isLoaded;

		if (isSizeOrPositionChanged || isNewMediaLoaded) {
			this.setState({
				width: isPercentageValues ? MediaRnd.getSizeInPx(width, frameWidth) : width,
				height: isPercentageValues ? MediaRnd.getSizeInPx(height, frameHeight) : height,
				x: getRoundedValue(isPercentageValues ? MediaRnd.getSizeInPx(x, frameWidth) : x),
				y: getRoundedValue(isPercentageValues ? MediaRnd.getSizeInPx(y, frameHeight) : y),
				isLoaded,
			});
		}

		// update store when item becomes inactive
		if (!isActive && isActive !== prevProps.isActive) {
			this.updateStore();
		}
	}

	componentWillUnmount() {
		this.updateStore();

		window.removeEventListener('keydown', this.handleKeyDown);

		const { iframeContainer } = this.props;

		if (iframeContainer) {
			iframeContainer.contentDocument!.removeEventListener('keydown', this.handleKeyDown);
		}
	}

	updateStore = () => {
		const {
			frameWidth,
			frameHeight,
			width,
			height,
			x,
			y,
			page,
			id,
			isLoaded,
			isCustom,
			isPercentageValues,
			onClickOutside,
			dispatch,
		} = this.props;
		const {
			width: currentWidth,
			height: currentHeight,
			x: currentX,
			y: currentY,
		} = this.state;
		const prevWidth = isPercentageValues ? MediaRnd.getSizeInPx(width, frameWidth) : width;
		const prevHeight = isPercentageValues ? MediaRnd.getSizeInPx(height, frameHeight) : height;
		const prevX = isPercentageValues ? MediaRnd.getSizeInPx(x, frameWidth) : x;
		const prevY = isPercentageValues ? MediaRnd.getSizeInPx(y, frameHeight) : y;
		const isSizeChanged = prevWidth !== currentWidth || prevHeight !== currentHeight;
		const isPositionChanged = prevX !== currentX || prevY !== currentY;

		if (isSizeChanged || isPositionChanged) {
			const positionAndSize = {
				width: isPercentageValues ? MediaRnd.getSizeInPercent(currentWidth, frameWidth) : currentWidth,
				height: isPercentageValues ? MediaRnd.getSizeInPercent(currentHeight, frameHeight) : currentHeight,
				x: isPercentageValues ? MediaRnd.getSizeInPercent(currentX, frameWidth) : currentX,
				y: isPercentageValues ? MediaRnd.getSizeInPercent(currentY, frameHeight) : currentY,
			};

			if (isCustom && onClickOutside) {
				onClickOutside(positionAndSize);
			} else {
				dispatch(updateEditableMedia(page, id, {
					...positionAndSize,
					isLoaded,
				}));
			}
		}
	}

	handleDragStop: RndDragCallback = (e, { x, y }) => {
		const { width, height, x: currentX, y: currentY } = this.state;
		const isPositionChanged = getRoundedValue(x) !== currentX || getRoundedValue(y) !== currentY;

		if (isPositionChanged) {
			this.setState({
				width,
				height,
				x: getRoundedValue(x),
				y: getRoundedValue(y),
			});
		}
	}

	handleResizeStart = () => {
		const { iframeContainer } = this.props;

		document.body.classList.add('no-user-select');

		if (iframeContainer && iframeContainer.contentDocument) {
			iframeContainer.contentDocument.body.classList.add('no-user-select');
		}
	}

	handleResize: RndResizeCallback = (e, direction, { offsetWidth, offsetHeight }, delta, { x, y }) => {
		// prevent scroll during resize on touch devices
		e.preventDefault();

		this.setState({
			width: offsetWidth,
			height: offsetHeight,
			x: getRoundedValue(x),
			y: getRoundedValue(y),
		});
	}

	handleResizeStop = () => {
		const { iframeContainer } = this.props;

		document.body.classList.remove('no-user-select');

		if (iframeContainer && iframeContainer.contentDocument) {
			iframeContainer.contentDocument.body.classList.remove('no-user-select');
		}
	}

	handleKeyDown = (e: KeyboardEvent) => {
		if (this.props.isKeyActionsDisabled) {
			return;
		}

		const { width, height, x, y } = this.state;
		let currentX = x;
		let currentY = y;

		e.preventDefault();

		if (keys.isArrowLeft(e.key)) {
			currentX -= 1;
		}
		if (keys.isArrowRight(e.key)) {
			currentX += 1;
		}
		if (keys.isArrowUp(e.key)) {
			currentY -= 1;
		}
		if (keys.isArrowDown(e.key)) {
			currentY += 1;
		}

		this.setState({
			width,
			height,
			x: getRoundedValue(currentX),
			y: getRoundedValue(currentY),
		});
	}

	render() {
		const {
			scale,
			isActive,
			isLoaded,
			children,
		} = this.props;
		const size = { width: this.state.width, height: this.state.height };
		const position = { x: this.state.x, y: this.state.y };
		const resizeHandleWrapperClass = this.state.isLoaded ? 'editor-media-controls' : '';
		const resizeHandleStyles = this.state.isLoaded ? MediaRnd.getResizeControlStyles(scale) : undefined;
		const mediaRndClasses = Utils.getCssClasses('uprez-media-rnd', {
			'is-active': isActive && isLoaded,
		});

		return (
			<StyledMediaRnd
				className={mediaRndClasses}
				isActive={isActive && isLoaded}
				outlineWidth={getBorderWidth(scale)}
			>
				<Rnd
					resizeHandleWrapperClass={resizeHandleWrapperClass}
					resizeHandleStyles={resizeHandleStyles}
					scale={scale}
					size={size}
					position={position}
					lockAspectRatio={true}
					onDragStop={this.handleDragStop}
					onResizeStart={this.handleResizeStart}
					onResize={this.handleResize}
					onResizeStop={this.handleResizeStop}
				>
					{children}
				</Rnd>
			</StyledMediaRnd>
		);
	}
}

export default MediaRnd;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/MediaEditor/MediaRnd/index.tsx